/**
 * 预订、追加预订
 */
import React, { useEffect, useRef, memo } from 'react';
import { message, Spin, Button } from 'antd';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';

import reserveService from '@/api/reserve';
import getRandomId from '@/utils/uuid';
import DateJS from '@/utils/date';
import { RoomCleanStatus } from '@/config/constant';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import usePortalConfirm from '@/components/PortalConfirm';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import { OrderDetailTabKey } from '@/pages/order/detail/component/DetailInfo';
import useOrderLink from '@/hooks/useOrderLink';

import PopupTarget, { TargetType } from '@/components/Reservation/PopupTarget';
import PreSettleDrawer, { PreSettleDrawerRef } from '@/components/Reservation/PreSettleDrawer';
import {
  showLeaveConfirm,
  showDirtyConfirm,
  showOutdateConfirm,
  showSameOrderConfirm,
} from '@/components/Reservation/Modal';
import {
  createStateSelector,
  flatRoomList,
  getRentTimeInfo,
} from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  refreshOutdateRoomInfo,
  getMainState,
} from '@/redux/actions/reservation/createReserve';
import { ReducerState, storeName } from '@/redux/reducers/reservation/createReserve';

import Notice from './components/Notice';
import CustomerInfo, { ReadOnlyCustomer } from './components/CustomerInfo';
import RentTimeInfoNode from './components/RentTimeInfo';
import ChannelInfoNode from './components/ChannelInfo';
import RoomSelector from './components/RoomSelector';
import RemarkInfo from './components/RemarkInfo';
import { validateSubmitData, validateStashData } from './validator';
import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading, commonInfo } = state;
  return { isInit, isLoading, isAppendMode: !!commonInfo.appendOrderId };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callRefreshRoomInfo: refreshOutdateRoomInfo,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  onEnd?: (success?: boolean) => void; // true 预订成功(跳转详情或刷新) false 预订取消 null 暂存
  appendOrderId?: string; // 追加预订 订单id
  tempOrderId?: string; // 暂存编辑 订单id
  defaultRoom?: Array<{ roomId: number; roomTypeId: number }>; // 默认选中房间
  defaultArrivalDate?: number; // 预订日期
}

interface IProps extends MapStateProps, MapDispatchProps, IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const {
    unmountNode,
    onEnd,
    isInit,
    isLoading,
    isAppendMode,
    callGetState,
    callSetLoading,
  } = props;

  const confirmPortal = usePortalConfirm();
  const preSettleRef = useRef<PreSettleDrawerRef>(null);
  const { linkToReservationDetail } = useOrderLink();

  // 获取暂存单详情
  const getTempOrderState = (id, callback) => {
    helper
      .getStateFromTemp(id)
      .then((data) => {
        if (data.message) {
          confirmPortal.showDrawer({
            title: intl.get('drawer_reserve_temp_message_data_lose'),
            content: data.message,
            confirmText: intl.get('drawer_reserve_temp_btn_continue_edit'),
            onCancel: () => {
              confirmPortal.closeDrawer();
            },
            onConfirm: () => {
              confirmPortal.closeDrawer();
            },
          });
        }
        callback(data.state);
      })
      .catch(() => unmountNode());
  };

  useEffect(() => {
    const { appendOrderId, tempOrderId, callInitState } = props;
    callInitState(() => {
      const arrivalDate = DateJS(props.defaultArrivalDate); // 默认时间 外部传入||当前时间
      const hourValue = arrivalDate.hour();
      /**
       * 若 00:00:00 ≤ 当前时间 ＜06:00:00，则默认置入当前时间
       * 若 06:00:00 ≤ 当前时间 ＜15:00:00，则默认置入15:00:00
       * 若 15:00:00 ≤ 当前时间 ＜00:00:00，则默认置入当前时间
       */
      if (hourValue >= 6 && hourValue < 15) {
        arrivalDate.startOf('day').add(15, 'hour');
      }
      return new Promise((resolve) => {
        if (appendOrderId) {
          // 追加预订
          helper
            .getStateFromMain(appendOrderId, { selectRoom: [], arrivalTime: arrivalDate.valueOf() })
            .then(resolve)
            .catch(() => unmountNode());
        } else if (tempOrderId) {
          // 暂存修改
          getTempOrderState(tempOrderId, resolve);
        } else {
          // 正常预订
          // 默认选中的房间
          const defaultSelectRoom = (props.defaultRoom || []).map((item) => ({
            indexId: getRandomId(),
            roomId: item.roomId,
            roomTypeId: item.roomTypeId,
          }));
          resolve(
            helper.getDefaultState({
              selectRoom: defaultSelectRoom,
              arrivalTime: arrivalDate.valueOf(),
            }),
          );
        }
      });
    });
  }, []);

  // 取消
  const handleEditCancel = () => {
    callGetState(async (state) => {
      const { commonInfo } = state;
      const isConfirm = await showLeaveConfirm(confirmPortal); // 是否确认离开
      if (isConfirm) {
        // 暂存编辑
        if (commonInfo.tempOrderId) {
          callSetLoading(true);
          try {
            await reserveService.deleteTempOrder(commonInfo.tempOrderId);
            message.success(intl.get('drawer_reserve_temp_message_delete_success'));
            onEnd(null);
          } catch (_) {
            //
            callSetLoading(false);
          }
        }
        unmountNode();
      }
    });
  };

  // 房间状态变动 需要重新刷新房间数据
  const handleRefreshErr: (response) => void = async (response) => {
    // 房间变动
    await showOutdateConfirm(confirmPortal, response, 'reserve').then((shouldRefresh) => {
      if (shouldRefresh) {
        const { callRefreshRoomInfo } = props;
        callRefreshRoomInfo();
      }
    });
  };

  // 入住脏房检查 距离12预抵小时内
  const checkDirtyRoom: () => Promise<void> = () => {
    return new Promise((resolve) => {
      callGetState(async (state) => {
        const { commonInfo, dayTypeTimeInfo, hourTypeTimeInfo, allRoomList, rentRoomInfos } = state;
        const { expectedArrivalTime } = getRentTimeInfo(
          { dayTypeTimeInfo, hourTypeTimeInfo },
          commonInfo.occType,
        );
        if (DateJS(expectedArrivalTime).diff(DateJS(), 'hours') > 12) {
          // 预抵时间在12小时以后
          resolve();
        } else {
          const roomData = flatRoomList(allRoomList);
          const dirtyList: { roomTypeName: string; roomNo: string; roomId: number }[] = [];
          rentRoomInfos.forEach((item) => {
            const { roomTypeId, roomId } = item;
            const { roomTypeName, roomListMap } = roomData[roomTypeId] || {};
            if (roomId && roomTypeName) {
              // 不是待排房
              const target = roomListMap[roomId];
              if (target && target.cleanStatus === RoomCleanStatus.Dirty) {
                // 脏房
                dirtyList.push({
                  roomTypeName,
                  roomId,
                  roomNo: target.roomNo,
                });
              }
            }
          });
          // 没有脏房
          if (!dirtyList.length) {
            resolve();
          } else {
            await showDirtyConfirm(confirmPortal, dirtyList);
            resolve();
          }
        }
      });
    });
  };

  // 预结算
  const showPreSettleDrawer = (orderNo) => {
    if (orderNo) {
      preSettleRef.current.showDrawer(orderNo, () => {
        message.success(intl.get('drawer_reserve_create_message_create_success'));
        unmountNode();
        if (isAppendMode) {
          // 追加预订
          if (onEnd) {
            onEnd(true);
          }
        } else {
          // 新预订
          linkToReservationDetail(orderNo, {
            tabKey: OrderDetailTabKey.RoomKey,
          });
        }
      });
    }
  };

  // 提交预订
  const handleSubmitClick = () => {
    callGetState(async (state) => {
      const err = validateSubmitData(state);
      if (err) {
        message.error(err);
        return;
      }
      const { commonInfo } = state;
      callSetLoading(true);
      let orderNo;
      try {
        const requestData = helper.formatToSubmitData(state);
        if (isAppendMode) {
          // 追加预订
          requestData.resvNo = commonInfo.appendOrderId;
          await reserveService.appendReserve(requestData).then(
            (res) => {
              if (res && res.data) {
                orderNo = res.data.resvNo;
              }
            },
            async (errRes) => {
              await handleRefreshErr(errRes);
              throw errRes;
            },
          );
        } else {
          // 新预订
          requestData.resvNo = commonInfo.tempOrderId || undefined;
          await reserveService.checkReserve(requestData).then(
            async (res) => {
              if (res.data?.sameOrderNo) {
                await showSameOrderConfirm(confirmPortal).then((shouldContinue) => {
                  if (!shouldContinue) {
                    // 不继续下单查看同名订单
                    linkToReservationDetail(res.data.sameOrderNo);
                    throw new Error(intl.get('drawer_reserve_create_message_order_conflict'));
                  }
                });
              }
            },
            async (errRes) => {
              await handleRefreshErr(errRes);
              throw errRes;
            },
          );
          await reserveService.createReserve(requestData).then(
            (res) => {
              if (res && res.data) {
                orderNo = res.data.resvNo;
              }
            },
            async (errRes) => {
              await handleRefreshErr(errRes);
              throw errRes;
            },
          );
        }
        await checkDirtyRoom();
        showPreSettleDrawer(orderNo);
      } catch (_) {
        callSetLoading(false);
      }
    });
  };

  // 预订暂存
  const handleStashClick = async () => {
    callGetState(async (state) => {
      try {
        const err = validateStashData(state);
        if (err) {
          message.error(err);
          return;
        }
        callSetLoading(true);
        const { commonInfo } = state;
        const requestData = helper.formatToTempData(state);
        if (commonInfo.tempOrderId) {
          requestData.resvNo = commonInfo.tempOrderId;
        }
        await reserveService.stashReserve(requestData);
        message.success(intl.get('drawer_reserve_temp_message_save_success'));
        unmountNode();
        onEnd(null);
      } catch (_) {
        callSetLoading(false);
      }
    });
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader
        title={intl.get(
          isAppendMode ? 'drawer_reserve_append_title' : 'drawer_reserve_create_title',
        )}
        onClose={handleEditCancel}
      />
      <div className={styles.pageContainer}>
        <div className={styles.noticeWrapper}>
          <Notice />
        </div>
        <div className={styles.mainContent}>
          <PopupTarget id={TargetType.Reserve} />
          {isAppendMode ? <ReadOnlyCustomer /> : <CustomerInfo />}
          <RentTimeInfoNode />
          <ChannelInfoNode />
          <RoomSelector />
          <RemarkInfo />
        </div>
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_reserve_create_btn_save')}
        </ThrottleButton>
        {isAppendMode ? null : (
          <ThrottleButton
            type="primary"
            ghost
            className={styles.stashBtn}
            onClick={handleStashClick}
          >
            {intl.get('drawer_reserve_temp_btn_save')}
          </ThrottleButton>
        )}
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <PreSettleDrawer ref={preSettleRef} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
